import { lazy, type ComponentType } from 'react'

/**
 * lazyWithRetry — drop-in replacement for React.lazy() that retries a failed
 * dynamic import() up to N times with exponential backoff.
 *
 * ROOT CAUSE THIS FIXES
 * ─────────────────────
 * In Firefox, when a route navigation happens while a chunk is mid-flight,
 * the fetch is aborted with NS_BINDING_ABORTED and the dynamic import()
 * rejects with:
 *   TypeError: error loading dynamically imported module: <url>
 * React 18 then surfaces this through its internal logCapturedError → bare
 * `console.error(Error)` (Firefox serializes the bare Error as just "Error").
 *
 * That uniform "console=1" pattern was observed on 19/19 adminclient/Firefox
 * cells in shard322 while Chromium passed all of them. The Chrome engine is
 * more tolerant: when an in-flight import is cancelled by navigation, it
 * generally completes from the HTTP cache once we re-enter the same route's
 * boundary.
 *
 * On a stale-deploy / partial-rollout / version-skew the same TypeError will
 * also fire because the requested chunk filename no longer exists at all —
 * retrying once with `?v=<ts>` cache-bust + then forcing a `location.reload()`
 * is the canonical Vite/CRA recovery (see e.g. vitejs/vite#11804 discussion).
 *
 * USAGE
 * ─────
 *   const HubPage = lazyWithRetry(() => import('./pages/HubPage'))
 *
 * Behaviour:
 *   - Retries on the FIRST failure (instant) and again after 600ms.
 *   - If still failing after retries AND the page is older than 60s, force
 *     a one-shot full page reload (handles stale-deploy chunk-not-found).
 *   - Otherwise re-throws so the Suspense boundary surfaces the failure.
 */
const RELOAD_KEY = '__sgrc_chunkReloadAt'

function shouldForceReload(): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_KEY) || '0')
    return Date.now() - last > 60_000
  } catch {
    return false
  }
}

function markForceReload() {
  try { sessionStorage.setItem(RELOAD_KEY, String(Date.now())) } catch { /* noop */ }
}

function isImportError(err: unknown): boolean {
  if (!err) return false
  const msg = (err instanceof Error ? err.message : String(err)).toLowerCase()
  // Firefox: "error loading dynamically imported module"
  // Chrome:  "Failed to fetch dynamically imported module"
  // Safari:  "Importing a module script failed."
  // Generic chunk-load: includes "chunk" or "loading css chunk"
  return (
    msg.includes('dynamically imported') ||
    msg.includes('importing a module') ||
    msg.includes('loading chunk') ||
    msg.includes('loading css chunk') ||
    msg.includes('failed to fetch') && msg.includes('module')
  )
}

export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
): ReturnType<typeof lazy<T>> {
  return lazy(async () => {
    try {
      return await factory()
    } catch (err1) {
      if (!isImportError(err1)) throw err1
      // First retry: immediate (covers Firefox NS_BINDING_ABORTED race).
      try {
        return await factory()
      } catch (err2) {
        if (!isImportError(err2)) throw err2
        // Second retry after 600ms backoff (covers transient network blips).
        await new Promise((r) => setTimeout(r, 600))
        try {
          return await factory()
        } catch (err3) {
          if (isImportError(err3) && shouldForceReload()) {
            // Likely stale deploy / chunk no longer exists. Force a fresh load.
            markForceReload()
            // eslint-disable-next-line no-console
            console.warn('[lazyWithRetry] chunk load failed 3x; forcing reload', err3)
            window.location.reload()
            // Return a never-resolving stub so React's Suspense waits for reload.
            return new Promise<{ default: T }>(() => {})
          }
          throw err3
        }
      }
    }
  })
}
